'use client'

/**
 * Conversation-level owner of the artifact preview and share dialogs.
 *
 * The transcript is virtualized, so a message row unmounts when it leaves the
 * overscan band — and it took its dialogs with it. Reachable without the user
 * scrolling at all: open a preview on an older message in a shared chat, a
 * participant's turn arrives, `use-conversation-scroll` scrolls to the bottom
 * to show it, the old row unmounts, and the dialog the user was reading
 * disappears.
 *
 * Persisting the row's `open` flag would be the WRONG fix — it would resurrect
 * a modal for a row that is off-screen, or nowhere. So ownership moves instead:
 * the row asks this host to show a preview, and the host holds the mounted
 * dialog. The row may come and go; the dialog does not care.
 *
 * The row still computes everything the dialog needs (urls, share eligibility,
 * the export menu element) and hands it over as a descriptor, so no
 * card-specific knowledge moves up here. The callbacks inside it stay valid
 * after the row dies: `onQuote` belongs to the conversation view, and the
 * first-value upsell drives a Zustand store — both outlive the row.
 *
 * Surfaces without a host — the public share viewer, the artifacts side panel,
 * the settings list, the landing demo — are not virtualized, so
 * {@link useArtifactPreviewSurface} keeps the dialogs local there. One
 * rendering implementation either way; only the owner differs.
 */

import { createContext, useContext, useMemo, useState } from 'react'
import type { ReactNode } from 'react'

import { MediaPreviewDialog } from '@/components/previews/media-preview-dialog'
import type { MessageArtifactElementQuote } from '@/lib/chat-types'
import { ShareDialog } from '@/components/share/share-dialog'

/** Everything the preview dialog needs, computed by the row that owns the card. */
export interface ArtifactPreviewDescriptor {
  /** Identity, so re-opening the same artifact replaces rather than stacks. */
  attachmentId: string
  url: string | null
  name: string
  contentType?: string | null
  description?: string | null
  size?: number | null
  downloadUrl?: string
  htmlPdfUrl?: string
  /** Present only when the artifact is shareable on this surface. */
  shareResourceId?: string
  onArtifactElementQuote?: (quote: MessageArtifactElementQuote) => void
  footerExtra?: ReactNode
  /** Fires on the open→close transition, for the first-value upsell. */
  onClosed?: () => void
}

/**
 * A dialog that has been opened at least once. `open` goes false on close but
 * the entry stays, because Radix plays its exit animation on the way out and
 * unmounting mid-flight would cut it. A closed Radix dialog renders no content,
 * so the retained entry costs one small object — far less than the per-row
 * dialogs it replaces.
 */
interface DialogSlot<T> {
  value: T
  open: boolean
}

const close = <T,>(slot: DialogSlot<T> | null) =>
  slot ? { ...slot, open: false } : slot

interface ArtifactPreviewHostValue {
  openPreview: (descriptor: ArtifactPreviewDescriptor) => void
}

const ArtifactPreviewHostContext =
  createContext<ArtifactPreviewHostValue | null>(null)

/**
 * The dialogs themselves. Shared so the hosted and the local paths render
 * exactly the same thing — the point of the host is WHERE this lives, never
 * WHAT it renders.
 */
function ArtifactPreviewSurface({
  preview,
  share,
  onClosePreview,
  onCloseShare,
  onRequestShare,
}: {
  preview: DialogSlot<ArtifactPreviewDescriptor> | null
  share: DialogSlot<string> | null
  onClosePreview: () => void
  onCloseShare: () => void
  onRequestShare: (attachmentId: string) => void
}) {
  const descriptor = preview?.value
  return (
    <>
      {descriptor ? (
        <MediaPreviewDialog
          // Keyed by artifact so opening a different one starts from a clean
          // dialog rather than inheriting the previous artifact's quote
          // selection — per-card state used to give that for free.
          // Namespaced: the share dialog is a sibling in this fragment and is
          // keyed by the SAME attachment id, so a bare id would collide.
          key={`preview:${descriptor.attachmentId}`}
          open={preview.open}
          onOpenChange={next => {
            // The slot is deliberately RETAINED in the closed state, so this
            // can be reached twice; the old per-card handler guarded on the
            // open→close transition (`if (!next && open)`) and dropping that
            // would double-fire the one-time upsell.
            if (next || !preview.open) return
            descriptor.onClosed?.()
            onClosePreview()
          }}
          url={descriptor.url}
          name={descriptor.name}
          contentType={descriptor.contentType ?? undefined}
          description={descriptor.description ?? undefined}
          size={descriptor.size ?? undefined}
          downloadUrl={descriptor.downloadUrl}
          htmlPdfUrl={descriptor.htmlPdfUrl}
          onShare={
            descriptor.shareResourceId
              ? () => onRequestShare(descriptor.shareResourceId as string)
              : undefined
          }
          sourceAttachmentId={descriptor.attachmentId}
          onArtifactElementQuote={descriptor.onArtifactElementQuote}
          footerExtra={descriptor.footerExtra}
        />
      ) : null}
      {share ? (
        <ShareDialog
          key={`share:${share.value}`}
          resourceType="chat_attachment"
          resourceId={share.value}
          open={share.open}
          onOpenChange={next => {
            if (!next) onCloseShare()
          }}
        />
      ) : null}
    </>
  )
}

/**
 * Mounted by `ChatMessageList` OUTSIDE the virtualizer, so it outlives every
 * row. Rendering the dialog only while a preview is active keeps the mounted
 * heavy-viewer count identical to the per-row arrangement it replaces.
 */
export function ArtifactPreviewHost({ children }: { children: ReactNode }) {
  const [preview, setPreview] =
    useState<DialogSlot<ArtifactPreviewDescriptor> | null>(null)
  const [share, setShare] = useState<DialogSlot<string> | null>(null)

  const value = useMemo<ArtifactPreviewHostValue>(
    () => ({
      openPreview: descriptor => setPreview({ value: descriptor, open: true }),
    }),
    [],
  )

  return (
    <ArtifactPreviewHostContext.Provider value={value}>
      {children}
      <ArtifactPreviewSurface
        preview={preview}
        share={share}
        onClosePreview={() => setPreview(close)}
        onCloseShare={() => setShare(close)}
        onRequestShare={id => setShare({ value: id, open: true })}
      />
    </ArtifactPreviewHostContext.Provider>
  )
}

/**
 * Used by the artifact card. Returns the openers plus, when there is no host,
 * the locally-owned dialogs to render.
 *
 * Both hooks run unconditionally, so hook order is stable either way; only the
 * result differs.
 */
export function useArtifactPreviewSurface(): {
  openPreview: (descriptor: ArtifactPreviewDescriptor) => void
  surface: ReactNode
} {
  const host = useContext(ArtifactPreviewHostContext)
  const [preview, setPreview] =
    useState<DialogSlot<ArtifactPreviewDescriptor> | null>(null)
  const [share, setShare] = useState<DialogSlot<string> | null>(null)

  if (host) {
    return { openPreview: host.openPreview, surface: null }
  }

  return {
    openPreview: descriptor => setPreview({ value: descriptor, open: true }),
    surface: (
      <ArtifactPreviewSurface
        preview={preview}
        share={share}
        onClosePreview={() => setPreview(close)}
        onCloseShare={() => setShare(close)}
        onRequestShare={id => setShare({ value: id, open: true })}
      />
    ),
  }
}
